/* eslint-disable no-unused-vars */
import React, { useMemo, useRef, useState } from "react";
import Modal from "react-modal";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FiFolder,
  FiGrid,
  FiList,
  FiSearch,
  FiPlus,
  FiUploadCloud,
  FiFileText,
  FiCheckCircle,
  FiEye,
  FiDownload,
  FiTrash2,
  FiArrowLeft,
  FiX,
  FiCpu,
  FiRefreshCw,
  FiZap,
  FiAlertTriangle,
} from "react-icons/fi";
// import { FaGoogleDrive } from "react-icons/fa";

import {
  useGetPiecesComptablesQuery,
  useGetPiecesStatsQuery,
  useLazyGetRapprochementPiecesQuery,
} from "actions/pieces/queries";
import {
  useCreatePieceComptableMutation,
  useDeletePieceComptableMutation,
  useRattacherPieceMutation,
} from "actions/pieces/mutations";
import { useUploadOcrFileMutation } from "actions/orc/mutations";
import { useLazyExtractOcrDataQuery } from "actions/orc/queries";
import {
  generateSamplePdfBlob,
  SAMPLE_DOCUMENTS_PRESETS,
} from "utils/sampleDocumentGenerator";
import { DEFAULT_BACKEND, DEFAULT_API } from "routes/api/endpoints";
import "utils/ConfirmationModal.css";
import HeaderFirst from "utils/HeaderFirst";
import TableLoader from "components/Loader/TableLoader";
import EcritureProposeeModal from "./EcritureProposeeModal";
import ConfirmModal from "utils/ConfirmationModal";
import { toastApiErrors } from "utils/toastApiError";
import { useDossierRole } from "contexts/DossierRoleContext";
import "../ventes/client.css";
import "../facturation/facturation.css";
import "./pieces.css";

if (typeof window !== "undefined") {
  Modal.setAppElement("#root");
}

const FOLDERS_SYSCOHADA = [
  { id: "411 - Ventes & Clients", code: "411", label: "Ventes & Clients", color: "#009688", bg: "#e0f7fa" },
  { id: "401 - Achats & Fournisseurs", code: "401", label: "Achats & Fournisseurs", color: "#e65100", bg: "#fff3e0" },
  { id: "52/57 - Trésorerie & Banques", code: "52/57", label: "Trésorerie & Banques", color: "#2e7d32", bg: "#e8f5e9" },
  { id: "44 - Fiscalité & Taxes", code: "44", label: "Fiscalité & Taxes", color: "#c2185b", bg: "#fce4ec" },
  { id: "42 - Paie & Personnel", code: "42", label: "Paie & Personnel", color: "#6a1b9a", bg: "#f3e5f5" },
  { id: "Divers SYSCOHADA", code: "DIVERS", label: "Divers SYSCOHADA", color: "#455a64", bg: "#eceff1" },
];

const DOC_TYPES = [
  "Facture Client",
  "Facture Fournisseur",
  "Reçu de Paiement",
  "Déclaration Fiscale",
  "Relevé Bancaire",
  "Contrat / Bon",
  "Autre",
];

const fmtMoney = (n) =>
  n ? new Intl.NumberFormat("fr-FR").format(n) + " FCFA" : "0 FCFA";

const resolveFileUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("blob:")) {
    return url;
  }
  const backendBase = DEFAULT_BACKEND || DEFAULT_API?.replace(/\/api\/?$/, "") || "http://127.0.0.1:8000";
  return `${backendBase}${url.startsWith("/") ? "" : "/"}${url}`;
};

const PiecesPage = () => {
  const { canEdit } = useDossierRole();
  const { clientID } = useParams();
  const navigate = useNavigate();
  const dossier_id = clientID;

  const [viewMode, setViewMode] = useState("dossiers"); // 'dossiers' | 'liste' | 'grille'
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedPartner, setSelectedPartner] = useState("all");

  // Modals
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAiImportModal, setShowAiImportModal] = useState(false);
  // const [showDriveModal, setShowDriveModal] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);

  // AI Import Form State
  const [aiDocName, setAiDocName] = useState("");
  const [aiFolder, setAiFolder] = useState("401 - Achats & Fournisseurs");
  const [aiFile, setAiFile] = useState(null);
  const [isAiScanning, setIsAiScanning] = useState(false);
  const [aiSuccess, setAiSuccess] = useState(false);
  const [isAiParsing, setIsAiParsing] = useState(false);
  const [aiConfidence, setAiConfidence] = useState(null);
  const aiFileInputRef = useRef(null);

  // Google Drive State (Désactivé pour la démo)
  // const [isDriveSyncing, setIsDriveSyncing] = useState(false);
  // const [driveSuccess, setDriveSuccess] = useState(false);
  // const [lastDriveSync, setLastDriveSync] = useState("Aujourd'hui à 11:45");

  // Standard Upload Form State
  const [formTitle, setFormTitle] = useState("");
  const [formPartner, setFormPartner] = useState("");
  const [formDocType, setFormDocType] = useState("Facture Fournisseur");
  const [formFolder, setFormFolder] = useState("401 - Achats & Fournisseurs");
  const [formAmount, setFormAmount] = useState("");
  const [formFile, setFormFile] = useState(null);
  // true quand Gemini a extrait un montant a la selection du fichier :
  // le backend peut alors sauter sa propre analyse docTR au submit.
  const [ocrValidated, setOcrValidated] = useState(false);
  const fileInputRef = useRef(null);

  // Import en masse (multi-fichiers)
  const [massUploading, setMassUploading] = useState(false);
  const [massProgress, setMassProgress] = useState("");
  const [showMassModal, setShowMassModal] = useState(false);
  const [massResult, setMassResult] = useState(null); // { ok, ko, rejected, rejectedFiles }
  const massInputRef = useRef(null);

  // Rapprochement pièces ↔ écritures (EC-18)
  const [showRapprochementModal, setShowRapprochementModal] = useState(false);
  const [rapprochementData, setRapprochementData] = useState(null);
  const [rapprochementLoading, setRapprochementLoading] = useState(false);
  const [triggerRapprochement] = useLazyGetRapprochementPiecesQuery();

  // API Queries & Mutations
  const { data: piecesData, isLoading: isPiecesLoading, isFetching: isPiecesFetching, refetch } = useGetPiecesComptablesQuery(
    { dossier_id, folder: selectedFolder, doc_type: selectedType, search },
    { skip: !dossier_id }
  );
  const { data: statsData } = useGetPiecesStatsQuery(
    { dossier_id },
    { skip: !dossier_id }
  );
  const [createPiece, { isLoading: isUploading }] = useCreatePieceComptableMutation();
  const [rattacherPiece] = useRattacherPieceMutation();
  const [attachTarget, setAttachTarget] = useState(null);
  const [ecritureModal, setEcritureModal] = useState({ open: false, piece: null });
  const [deletePiece] = useDeletePieceComptableMutation();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [pieceToDelete, setPieceToDelete] = useState(null);
  const [uploadOcrFile] = useUploadOcrFileMutation();
  const [triggerExtractOcrData] = useLazyExtractOcrDataQuery();

  const documents = useMemo(() => piecesData?.documents || [], [piecesData]);

  // Unique list of partners
  const partners = useMemo(() => {
    const set = new Set(documents.map((d) => d.partnerName || d.partenaire).filter(Boolean));
    return Array.from(set);
  }, [documents]);

  // Filtered documents
  const filteredDocs = useMemo(() => {
    return documents.filter((doc) => {
      const matchSearch =
        !search ||
        (doc.title && doc.title.toLowerCase().includes(search.toLowerCase())) ||
        (doc.fileName && doc.fileName.toLowerCase().includes(search.toLowerCase())) ||
        (doc.partnerName && doc.partnerName.toLowerCase().includes(search.toLowerCase()));

      const matchType = selectedType === "all" || doc.docType === selectedType || doc.doc_type === selectedType;
      const matchPartner = selectedPartner === "all" || (doc.partnerName || doc.partenaire) === selectedPartner;
      const matchFolder = !selectedFolder || (doc.folder || doc.dossier_syscohada) === selectedFolder;

      return matchSearch && matchType && matchPartner && matchFolder;
    });
  }, [documents, search, selectedType, selectedPartner, selectedFolder]);

  // Une preuve de facture = montant TTC detecte + au moins un element
  // d'identite (emetteur, destinataire, reference de facture ou lignes
  // detaillees). Sans ca, un nombre quelconque (budget, ID, telephone)
  // peut etre pris pour un montant et faire sauter la verification docTR.
  const ocrLooksLikeInvoice = (ocrResponse, totalAmount) => {
    if (!totalAmount) return false;
    const details = ocrResponse?.invoice_details || {};
    const items = ocrResponse?.line_items || [];
    const nonEmpty = (keys) => keys.some((k) => `${details?.[k] ?? ""}`.trim() !== "");
    return (
      nonEmpty(["supplier_name", "vendor_name", "fournisseur", "seller", "emetteur"]) ||
      nonEmpty(["customer_name", "client_name", "client", "buyer"]) ||
      nonEmpty(["invoice_number", "invoice_ref", "reference", "numero_facture"]) ||
      (Array.isArray(items) && items.length > 0)
    );
  };

  // Process File with real OCR (same backend as Assistant IA) + fallback by filename
  const processFileWithAi = async (file, presetHint = null) => {
    setFormFile(file);
    setIsAiParsing(true);

    if (presetHint) {
      setTimeout(() => {
        setFormTitle(presetHint.title);
        setFormPartner(presetHint.partner);
        setFormFolder(presetHint.folder);
        setFormDocType(presetHint.docType);
        setFormAmount(presetHint.amount.toString());
        setOcrValidated(false);
        setAiConfidence("Pré-rempli depuis le dossier courant");
        setIsAiParsing(false);
      }, 700);
      return;
    }

    const fileNameLower = (file.name || "").toLowerCase();
    const cleanTitle = (file.name || "Piece_Comptable")
      .replace(/\.[^/.]+$/, "")
      .replace(/[-_]/g, " ");

    // 1. Détection des documents non-comptables (guides, docs textuels, tutos, readme)
    const isNonAccounting =
      fileNameLower.includes("guide") ||
      fileNameLower.includes("manuel") ||
      fileNameLower.includes("tutoriel") ||
      fileNameLower.includes("tuto") ||
      fileNameLower.includes("readme") ||
      fileNameLower.includes("presentation") ||
      fileNameLower.includes("rapport") ||
      fileNameLower.includes("note") ||
      fileNameLower.includes("cours");

    if (isNonAccounting) {
      setFormTitle(cleanTitle);
      setFormPartner("Document Interne / Divers");
      setFormFolder("Divers SYSCOHADA");
      setFormDocType("Autre");
      setFormAmount("");
      setOcrValidated(false);
      setAiConfidence("Document non financier : classé par défaut en 'Divers SYSCOHADA'");
      setIsAiParsing(false);
      toast.warning("Ce fichier semble être un document d'information. Classé en 'Divers SYSCOHADA'.");
      return;
    }

    // 2. Appel au vrai backend OCR (Gemini via Bakeli : même que l'Assistant IA)
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("dossier_client", dossier_id);
      const uploadResponse = await uploadOcrFile(formData).unwrap();
      if (!uploadResponse?.id) {
        throw new Error("Upload OCR échoué");
      }

      const ocrResponse = await triggerExtractOcrData(uploadResponse.id).unwrap();

      // Normalisation de la réponse OCR (même logique que AssistantIADetailPage)
      const details = ocrResponse?.invoice_details || {};
      const totals = ocrResponse?.totals || {};
      const pickVal = (obj, keys) => {
        for (const k of keys) {
          const v = obj?.[k];
          if (v !== undefined && v !== null && `${v}`.trim() !== "") return `${v}`.trim();
        }
        return "";
      };

      const supplierName = pickVal(details, ["supplier_name", "vendor_name", "fournisseur", "seller", "emetteur"]);
      const customerName = pickVal(details, ["customer_name", "client_name", "client", "buyer"]);
      const invoiceNumber = pickVal(details, ["invoice_number", "invoice_ref", "reference", "numero_facture"]);
      const totalAmount = pickVal(totals, ["total_amount_due", "total_ttc", "total_amount", "montant_ttc", "net_a_payer"]) ||
        pickVal(details, ["total_amount", "montant_total"]);

      const partner = supplierName || customerName || "Tiers à identifier";
      const amount = totalAmount ? String(Math.round(Number(totalAmount) || 0)) : "";

      // Détection du dossier SYSCOHADA selon le type de tiers
      let folder = "401 - Achats & Fournisseurs";
      let docType = "Facture Fournisseur";
      if (supplierName && !customerName) {
        folder = "401 - Achats & Fournisseurs";
        docType = "Facture Fournisseur";
      } else if (customerName && !supplierName) {
        folder = "411 - Ventes & Clients";
        docType = "Facture Client";
      } else if (fileNameLower.includes("releve") || fileNameLower.includes("banque")) {
        folder = "52/57 - Trésorerie & Banques";
        docType = "Relevé Bancaire";
      } else if (fileNameLower.includes("fisc") || fileNameLower.includes("tva") || fileNameLower.includes("impot")) {
        folder = "44 - Fiscalité & Taxes";
        docType = "Déclaration Fiscale";
      } else if (fileNameLower.includes("salaire") || fileNameLower.includes("paie") || fileNameLower.includes("bulletin")) {
        folder = "42 - Paie & Personnel";
        docType = "Reçu de Paiement";
      }

      setFormTitle(invoiceNumber ? `Facture ${invoiceNumber}` : cleanTitle);
      setFormPartner(partner);
      setFormFolder(folder);
      setFormDocType(docType);
      setFormAmount(amount);
      const factureProbable = ocrLooksLikeInvoice(ocrResponse, totalAmount);
      setOcrValidated(factureProbable);
      setAiConfidence(
        amount
          ? factureProbable
            ? `Extrait par OCR (Gemini) : montant ${Number(amount).toLocaleString("fr-FR")} FCFA`
            : `Montant ${Number(amount).toLocaleString("fr-FR")} FCFA lu, mais le document ne ressemble pas a une facture : il sera reverifie a l'enregistrement.`
          : "OCR effectué : montant non détecté, à saisir"
      );
      setIsAiParsing(false);
      toast.success("OCR réussi : champs pré-remplis. Vérifiez et validez.");
    } catch (err) {
      console.error("Erreur OCR:", err);
      // Fallback : détection par nom de fichier si l'OCR échoue
      let partner = "Tiers à identifier";
      let folder = "401 - Achats & Fournisseurs";
      let docType = "Facture Fournisseur";

      if (fileNameLower.includes("orange") || fileNameLower.includes("mtn") || fileNameLower.includes("moov") || fileNameLower.includes("telecom")) {
        partner = "Télécom"; folder = "401 - Achats & Fournisseurs"; docType = "Facture Fournisseur";
      } else if (fileNameLower.includes("cie") || fileNameLower.includes("senelec") || fileNameLower.includes("sodeci") || fileNameLower.includes("electricite") || fileNameLower.includes("eau")) {
        partner = "CIE / SODECI"; folder = "401 - Achats & Fournisseurs"; docType = "Facture Fournisseur";
      } else if (fileNameLower.includes("client") || fileNameLower.includes("vente") || fileNameLower.includes("fac-") || fileNameLower.includes("devis")) {
        partner = "Client à identifier"; folder = "411 - Ventes & Clients"; docType = "Facture Client";
      } else if (fileNameLower.includes("releve") || fileNameLower.includes("banque") || fileNameLower.includes("virement")) {
        partner = "Banque"; folder = "52/57 - Trésorerie & Banques"; docType = "Relevé Bancaire";
      } else if (fileNameLower.includes("fisc") || fileNameLower.includes("tva") || fileNameLower.includes("impot")) {
        partner = "Administration fiscale"; folder = "44 - Fiscalité & Taxes"; docType = "Déclaration Fiscale";
      } else if (fileNameLower.includes("salaire") || fileNameLower.includes("paie") || fileNameLower.includes("bulletin")) {
        partner = "Personnel & Salariés"; folder = "42 - Paie & Personnel"; docType = "Reçu de Paiement";
      }

      setFormTitle(cleanTitle);
      setFormPartner(partner);
      setFormFolder(folder);
      setFormDocType(docType);
      setFormAmount("");
      setOcrValidated(false);
      setAiConfidence("OCR indisponible (service externe down) : dossier détecté par le nom. Saisissez le montant manuellement.");
      setIsAiParsing(false);
      // Afficher le vrai message renvoye par le backend (ex. Assistant IA indisponible)
      toastApiErrors(err);
      toast.warning("Dossier détecté par le nom du fichier. Saisissez le montant manuellement.");
    }
  };

  // Handle File Input Change
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      processFileWithAi(file);
    }
  };

  // Reinitialisation des formulaires a la fermeture (Annuler / succes)
  const resetUploadForm = () => {
    setFormFile(null);
    setFormTitle("");
    setFormPartner("");
    setFormAmount("");
    setFormDocType("Facture Fournisseur");
    setOcrValidated(false);
    setAiConfidence(null);
    setIsAiParsing(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  const closeUploadModal = () => {
    resetUploadForm();
    setShowUploadModal(false);
  };
  const resetAiForm = () => {
    setAiDocName("");
    setAiFile(null);
    setAiConfidence(null);
    if (aiFileInputRef.current) aiFileInputRef.current.value = "";
  };
  const closeAiModal = () => {
    if (isAiScanning) return;
    resetAiForm();
    setShowAiImportModal(false);
  };

  // Manual Upload Submit
  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    let fileToUpload = formFile;
    if (!fileToUpload) {
      // Si aucun fichier choisi, générer un PDF minimal de test
      fileToUpload = generateSamplePdfBlob(
        formTitle || "Piece_Comptable.pdf",
        formPartner || "Tiers",
        Number(formAmount) || 100000,
        formFolder,
        formDocType
      );
    }

    if (!dossier_id) {
      toast.error("Dossier introuvable.");
      return;
    }

    const formData = new FormData();
    formData.append("dossier_id", dossier_id);
    formData.append("file", fileToUpload);
    formData.append("title", formTitle || fileToUpload.name);
    formData.append("partnerName", formPartner);
    formData.append("docType", formDocType);
    formData.append("folder", formFolder);
    // CORRECTION : Ne pas envoyer le montant s'il est vide ou "0" pour éviter d'enregistrer 0
    if (formAmount && Number(formAmount) > 0) {
      formData.append("amount", formAmount);
      // Gemini a deja analyse ce fichier a la selection : le backend saute
      // sa propre extraction docTR (enregistrement quasi instantane).
      if (ocrValidated) {
        formData.append("ocr_validated", "gemini");
      }
    }

    try {
      await createPiece(formData).unwrap();
      toast.success("Document classé et écriture comptable générée !");
      closeUploadModal();
      refetch();
    } catch (error) {
      toastApiErrors(error);
    }
  };

  // Rapprochement pièces GED ↔ écritures : page dédiée
  const handleRapprochement = () => {
    navigate(`../rapprochement-pieces/`);
  };

  // Rattacher une piece existante a une ecriture orpheline
  const handleRattacherPiece = async (piece) => {
    if (!attachTarget) return;
    try {
      await rattacherPiece({
        piece_id: piece.id,
        target_type: "ecriture",
        target_id: attachTarget.id,
        dossier_id,
      }).unwrap();
      toast.success(`« ${piece.titre} » rattachée à l'écriture.`);
      setAttachTarget(null);
      const result = await triggerRapprochement({ dossier_id }).unwrap();
      setRapprochementData(result);
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || "Impossible de rattacher la pièce.");
    }
  };

  // Comptabiliser une piece orpheline : ouvre la modale OCR de proposition
  // d'ecriture (MVP CECA) au lieu de l'action auto. L'utilisateur verifie,
  // corrige puis valide. La boucle d'apprentissage viendra ensuite pour
  // atteindre 95-97% de justesse.
  const handleComptabiliserPiece = (p) => {
    setEcritureModal({ open: true, piece: p });
  };

  const handleEcritureValidee = async () => {
    toast.success("Écriture comptabilisée avec succès.");
    try {
      const result = await triggerRapprochement({ dossier_id }).unwrap();
      setRapprochementData(result);
    } catch (_) { /* la modale reste ouverte, l'utilisateur peut reessayer */ }
    refetch();
  };

  // Import en masse : un POST par fichier, métadonnées à compléter ensuite
  const handleMassUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length || !dossier_id || massUploading) return;
    setShowMassModal(true);
    setMassResult(null);
    setMassUploading(true);
    let ok = 0;
    let ko = 0;
    let rejected = 0;
    const rejectedFiles = [];
    const folder = selectedFolder || "Divers SYSCOHADA";

    // Helper local : detection du dossier SYSCOHADA selon le nom de fichier
    const detectFolderByFileName = (name) => {
      const n = (name || "").toLowerCase();
      if (n.includes("client") || n.includes("vente") || n.includes("fac-") || n.includes("devis")) {
        return { folder: "411 - Ventes & Clients", docType: "Facture Client", partner: "Client a identifier" };
      }
      if (n.includes("releve") || n.includes("banque") || n.includes("virement")) {
        return { folder: "52/57 - Tresorerie & Banques", docType: "Releve Bancaire", partner: "Banque" };
      }
      if (n.includes("fisc") || n.includes("tva") || n.includes("impot")) {
        return { folder: "44 - Fiscalite & Taxes", docType: "Declaration Fiscale", partner: "Administration fiscale" };
      }
      if (n.includes("salaire") || n.includes("paie") || n.includes("bulletin")) {
        return { folder: "42 - Paie & Personnel", docType: "Recu de Paiement", partner: "Personnel & Salaries" };
      }
      if (n.includes("orange") || n.includes("mtn") || n.includes("senelec") || n.includes("sde")) {
        return { folder: "401 - Achats & Fournisseurs", docType: "Facture Fournisseur", partner: "Fournisseur a identifier" };
      }
      return null;
    };

    const pickVal = (obj, keys) => {
      for (const k of keys) {
        const v = obj?.[k];
        if (v !== undefined && v !== null && `${v}`.trim() !== "") return `${v}`.trim();
      }
      return "";
    };

    // Pipeline par fichier : upload OCR -> extraction Gemini -> createPiece.
    // Traitement par paquets de 3 en parallele : le temps total est divise
    // par ~3 (le goulot est l'appel Gemini, pas le CPU du navigateur).
    let processed = 0;
    const processOne = async (file) => {
      // 1. Valeurs par defaut selon le nom de fichier (fallback si OCR down)
      let autoPartner = "Tiers a identifier";
      let autoDocType = "Autre";
      let autoAmount = "";
      let geminiValide = false;
      const detected = detectFolderByFileName(file.name);
      if (detected) {
        autoPartner = detected.partner;
        autoDocType = detected.docType;
      }

      // 2. Tentative OCR Gemini (comme handleAiImportSubmit)
      try {
        const ocrFormData = new FormData();
        ocrFormData.append("file", file);
        ocrFormData.append("dossier_client", dossier_id);
        const uploadResponse = await uploadOcrFile(ocrFormData).unwrap();
        if (uploadResponse?.id) {
          const ocrResponse = await triggerExtractOcrData(uploadResponse.id).unwrap();
          const details = ocrResponse?.invoice_details || {};
          const totals = ocrResponse?.totals || {};
          const supplierName = pickVal(details, ["supplier_name", "vendor_name", "fournisseur", "seller"]);
          const customerName = pickVal(details, ["customer_name", "client_name", "client", "buyer"]);
          const totalAmount = pickVal(totals, ["total_amount_due", "total_ttc", "total_amount", "montant_ttc", "net_a_payer"]) ||
            pickVal(details, ["total_amount", "montant_total"]);
          if (supplierName) autoPartner = supplierName;
          else if (customerName) autoPartner = customerName;
          if (totalAmount) {
            autoAmount = String(Math.round(Number(totalAmount) || 0));
            geminiValide = ocrLooksLikeInvoice(ocrResponse, totalAmount);
          }
          if (supplierName && !customerName) { autoDocType = "Facture Fournisseur"; }
          else if (customerName && !supplierName) { autoDocType = "Facture Client"; }
        }
      } catch (ocrErr) {
        // OCR Gemini indisponible : on continue avec les valeurs par defaut.
        // Le backend fera son propre OCR docTR (maintenant fonctionnel pour tous fichiers)
        // et rejettera les documents non factures.
        console.warn("OCR Gemini indisponible pour", file.name, ocrErr);
      }

      // 3. Envoi au backend (avec montant Gemini si trouve, sinon backend fera son OCR)
      const formData = new FormData();
      formData.append("dossier_id", dossier_id);
      formData.append("file", file);
      formData.append(
        "title",
        file.name.replace(/\.[^/.]+$/, "").replace(/[-_]/g, " ")
      );
      formData.append("docType", autoDocType);
      formData.append("folder", detected ? detected.folder : folder);
      formData.append("status", "En revision");
      formData.append("partnerName", autoPartner);
      // N'envoyer le montant que si Gemini l'a trouve (sinon backend fait son OCR)
      if (autoAmount && Number(autoAmount) > 0) {
        formData.append("amount", autoAmount);
        // docTR saute seulement si le document a de vrais indices de facture
        // (montant + emetteur/reference/lignes) : un simple nombre ne suffit pas.
        if (geminiValide) {
          formData.append("ocr_validated", "gemini");
        }
      }
      formData.append(
        "aiClassification",
        autoAmount
          ? `Extrait par OCR (Gemini) : ${Number(autoAmount).toLocaleString("fr-FR")} FCFA`
          : "Import masse : OCR backend (montant a extraire)"
      );

      try {
        await createPiece(formData).unwrap();
        ok++;
      } catch (err) {
        const reason = err?.data?.message;
        // Si le backend a rejete (non facture), on compte en rejets
        if (reason && /ne semble pas etre une facture|aucun montant|impossible de verifier/i.test(reason)) {
          rejected++;
          rejectedFiles.push(file.name);
        } else {
          ko++;
        }
      }
      processed++;
      setMassProgress(`${processed}/${files.length} fichier(s) traite(s)...`);
    };

    const CONCURRENCE = 3;
    for (let i = 0; i < files.length; i += CONCURRENCE) {
      await Promise.all(files.slice(i, i + CONCURRENCE).map(processOne));
    }
    setMassUploading(false);
    setMassProgress("");
    setMassResult({ ok, ko, rejected, rejectedFiles });
    refetch();
    // Succes total : fermer le modal automatiquement apres un court delai.
    // Rejets ou erreurs : laisser le modal ouvert pour que l'utilisateur
    // puisse importer une vraie facture.
    if (!ko && !rejected) {
      setTimeout(() => {
        setShowMassModal(false);
        setMassResult(null);
      }, 1500);
      toast.success(`${ok} piece(s) importee(s) avec OCR.`);
    }
  };

  // AI OCR Import Submit (Flux express avec vrai OCR Gemini + déduction SYSCOHADA)
  const handleAiImportSubmit = async (e) => {
    e.preventDefault();
    if (!aiDocName.trim() && !aiFile) {
      toast.error("Veuillez déposer un document ou saisir un intitulé.");
      return;
    }

    setIsAiScanning(true);
    const finalDocName = aiDocName.trim() || (aiFile ? aiFile.name.replace(/\.[^/.]+$/, "") : `Piece_${Date.now()}`);

    // Valeurs par défaut selon le dossier SYSCOHADA sélectionné
    let autoPartner = "Tiers à identifier";
    let autoDocType = "Autre";
    let autoAmount = "";
    let geminiValide = false;

    if (aiFolder.includes("411") || aiFolder.includes("Ventes")) {
      autoPartner = "Client à identifier";
      autoDocType = "Facture Client";
    } else if (aiFolder.includes("52") || aiFolder.includes("Banques")) {
      autoPartner = "Banque à identifier";
      autoDocType = "Relevé Bancaire";
    } else if (aiFolder.includes("44") || aiFolder.includes("Fiscalité")) {
      autoPartner = "Administration fiscale";
      autoDocType = "Déclaration Fiscale";
    } else if (aiFolder.includes("42") || aiFolder.includes("Paie")) {
      autoPartner = "Personnel & Salariés";
      autoDocType = "Reçu de Paiement";
    } else if (aiFolder.includes("401") || aiFolder.includes("Achats")) {
      autoPartner = "Fournisseur à identifier";
      autoDocType = "Facture Fournisseur";
    }

    try {
      // Si un fichier est fourni, on tente le vrai OCR (Gemini via Bakeli)
      let finalDocNameUpdated = finalDocName;
      if (aiFile) {
        try {
          const ocrFormData = new FormData();
          ocrFormData.append("file", aiFile);
          ocrFormData.append("dossier_client", dossier_id);
          const uploadResponse = await uploadOcrFile(ocrFormData).unwrap();
          if (uploadResponse?.id) {
            const ocrResponse = await triggerExtractOcrData(uploadResponse.id).unwrap();
            const details = ocrResponse?.invoice_details || {};
            const totals = ocrResponse?.totals || {};
            const pickVal = (obj, keys) => {
              for (const k of keys) {
                const v = obj?.[k];
                if (v !== undefined && v !== null && `${v}`.trim() !== "") return `${v}`.trim();
              }
              return "";
            };
            const supplierName = pickVal(details, ["supplier_name", "vendor_name", "fournisseur", "seller"]);
            const customerName = pickVal(details, ["customer_name", "client_name", "client", "buyer"]);
            const invoiceNumber = pickVal(details, ["invoice_number", "invoice_ref", "reference", "numero_facture"]);
            const totalAmount = pickVal(totals, ["total_amount_due", "total_ttc", "total_amount", "montant_ttc", "net_a_payer"]) ||
              pickVal(details, ["total_amount", "montant_total"]);

            if (supplierName) autoPartner = supplierName;
            else if (customerName) autoPartner = customerName;
            if (totalAmount) {
              autoAmount = String(Math.round(Number(totalAmount) || 0));
              geminiValide = ocrLooksLikeInvoice(ocrResponse, totalAmount);
            }
            if (invoiceNumber) finalDocNameUpdated = `Facture ${invoiceNumber}`;
          }
        } catch (ocrErr) {
          // L'OCR a échoué, on continue avec les valeurs par défaut
          console.warn("OCR indisponible, fallback valeurs par défaut", ocrErr);
        }
      }

      const fileToSend =
        aiFile ||
        generateSamplePdfBlob(
          finalDocName,
          autoPartner,
          autoAmount || 0,
          aiFolder,
          autoDocType
        );

      const formData = new FormData();
      formData.append("dossier_id", dossier_id);
      formData.append("file", fileToSend);
      formData.append("title", finalDocNameUpdated);
      formData.append("partnerName", autoPartner);
      formData.append("docType", autoDocType);
      formData.append("folder", aiFolder);
      // CORRECTION : Ne pas envoyer le montant s'il est vide ou "0" pour éviter d'enregistrer 0
      if (autoAmount && Number(autoAmount) > 0) {
        formData.append("amount", autoAmount);
        // docTR saute seulement si le document a de vrais indices de facture
        // (montant + emetteur/reference/lignes) : un simple nombre ne suffit pas.
        if (geminiValide) {
          formData.append("ocr_validated", "gemini");
        }
      }
      formData.append(
        "aiClassification",
        autoAmount
          ? `Extrait par OCR (Gemini) : ${Number(autoAmount).toLocaleString("fr-FR")} FCFA`
          : "Dossier SYSCOHADA pré-sélectionné (montant à saisir)"
      );

      await createPiece(formData).unwrap();
      setIsAiScanning(false);
      setAiSuccess(true);
      refetch();

      setTimeout(() => {
        setAiSuccess(false);
        setShowAiImportModal(false);
        resetAiForm();
        toast.success(autoAmount
          ? "Document analysé par OCR et classé avec succès !"
          : "Document classé. Saisissez le montant depuis la liste."
        );
      }, 1200);
    } catch (err) {
      setIsAiScanning(false);
      toastApiErrors(err);
    }
  };

  // 1-Click Demo Injector (Crée 3 pièces pour tester immédiatement)
  const handleSeedDemoPieces = async () => {
    if (!dossier_id) return;
    toast.info("Génération de 3 pièces comptables de test...");
    for (let i = 0; i < 3; i++) {
      const p = SAMPLE_DOCUMENTS_PRESETS[i];
      const file = generateSamplePdfBlob(p.title, p.partner, p.amount, p.folder, p.docType);
      const formData = new FormData();
      formData.append("dossier_id", dossier_id);
      formData.append("file", file);
      formData.append("title", p.title);
      formData.append("partnerName", p.partner);
      formData.append("docType", p.docType);
      formData.append("folder", p.folder);
      formData.append("amount", p.amount.toString());
      formData.append("aiClassification", p.aiClassification);
      try {
        await createPiece(formData).unwrap();
      } catch (e) {
        // ignore
      }
    }
    refetch();
    toast.success("3 pièces d'exemple créées avec succès !");
  };

  // Google Drive Sync Simulation (Désactivé pour la démo)
  // const handleDriveSync = () => {
  //   setIsDriveSyncing(true);
  //   setTimeout(() => {
  //     setIsDriveSyncing(false);
  //     setDriveSuccess(true);
  //     setLastDriveSync(`Aujourd'hui à ${new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`);
  //     setTimeout(() => setDriveSuccess(false), 3000);
  //   }, 1500);
  // };

  // Delete Piece : ouvre le modal de confirmation (comme les transactions)
  const handleDelete = (id) => {
    setPieceToDelete(id);
    setIsConfirmModalOpen(true);
  };

  const handleDeleteConfirmation = async () => {
    if (!pieceToDelete) return;
    try {
      await deletePiece({ id: pieceToDelete }).unwrap();
      toast.success("Document supprimé.");
      if (previewDoc && previewDoc.id === pieceToDelete) setPreviewDoc(null);
      setIsConfirmModalOpen(false);
      setPieceToDelete(null);
      refetch();
    } catch (error) {
      toast.error("Erreur lors de la suppression.");
      setIsConfirmModalOpen(false);
      setPieceToDelete(null);
    }
  };

  // Download Trigger
  const handleDownload = (doc) => {
    const rawUrl = doc.fileUrl || doc.fichier;
    if (rawUrl) {
      const fullUrl = resolveFileUrl(rawUrl);
      const a = document.createElement("a");
      a.href = fullUrl;
      a.download = doc.fileName || `${doc.title}.pdf`;
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      // Télécharger un PDF généré à la volée
      const sampleFile = generateSamplePdfBlob(
        doc.title || "Piece_Comptable.pdf",
        doc.partnerName || "Tiers",
        doc.amount || 0,
        doc.folder || "Divers SYSCOHADA",
        doc.docType || "Autre"
      );
      const url = URL.createObjectURL(sampleFile);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${doc.title || "piece"}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
    toast.success(`Téléchargement de ${doc.fileName || doc.title}...`);
  };

  const nom = (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <h4 className="titre-dossier" style={{ margin: 0 }}>Pièces Comptables &amp; GED</h4>
      <span className="nbrDossier">{documents.length}</span>
    </div>
  );
  const description = (
    <div>
      {statsData?.total_pieces ?? documents.length} pièce(s) classée(s) · {fmtMoney(statsData?.total_montant || 0)} documentés
    </div>
  );

  return (
    <div
      className="global"
      style={{
        backgroundColor: "white",
        minHeight: "500px",
        borderRadius: "20px",
        padding: "15px",
      }}
    >
      {/* Header unifié avec HeaderFirst */}
      <div
        className="header pb-3"
        style={{
          backgroundColor: "white",
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <HeaderFirst nom={nom} description={description} />

        <div style={{ display: canEdit ? "flex" : "none", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {/* AI Import Button */}
          <button
            type="button"
            className="fact-btn"
            style={{
              background: "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)",
              color: "#ffffff",
              border: "none",
              borderRadius: "31px",
              padding: "8px 16px",
              fontSize: "13px",
              fontWeight: 600,
              boxShadow: "0 2px 6px rgba(124, 58, 237, 0.25)",
            }}
            onClick={() => setShowAiImportModal(true)}
          >
            <FiZap size={14} color="#fde047" style={{ marginRight: 6 }} />
            <span>Importer une pièce (IA)</span>
          </button>

          {/* Scanner / Ajouter Button */}
          <button
            type="button"
            className="fact-btn fact-btn-solid-teal"
            style={{ borderRadius: "31px", padding: "8px 18px", fontSize: "13px", fontWeight: 600 }}
            onClick={() => setShowUploadModal(true)}
          >
            <FiUploadCloud size={15} style={{ marginRight: 6 }} />
            <span>Scanner / Ajouter</span>
          </button>

          {/* Import en masse */}
          <input
            ref={massInputRef}
            type="file"
            multiple
            accept=".pdf,.png,.jpg,.jpeg"
            style={{ display: "none" }}
            onChange={handleMassUpload}
          />
          <button
            type="button"
            className="fact-btn"
            style={{
              borderRadius: "31px",
              padding: "8px 18px",
              fontSize: "13px",
              fontWeight: 600,
              border: "1px solid #009688",
              color: "#009688",
              background: "#fff",
            }}
            disabled={massUploading}
            onClick={() => massInputRef.current?.click()}
            title="Importer plusieurs fichiers d'un coup (classés en 'En révision' dans le dossier courant)"
          >
            <FiUploadCloud size={15} style={{ marginRight: 6 }} />
            <span>{massUploading ? massProgress || "Import en cours..." : "Import en masse"}</span>
          </button>

          {/* Rapprochement pièces GED ↔ écritures (EC-18) */}
          <button
            type="button"
            className="fact-btn"
            style={{
              borderRadius: "31px",
              padding: "8px 18px",
              fontSize: "13px",
              fontWeight: 600,
              border: "1px solid #6366f1",
              color: "#6366f1",
              background: "#fff",
            }}
            onClick={handleRapprochement}
            title="Vérifier la correspondance entre les pièces GED et les écritures comptables"
          >
            <FiSearch size={15} style={{ marginRight: 6 }} />
            <span>Rapprochement</span>
          </button>


        </div>
      </div>

      {/* KPI Cards */}
      <div className="fact-kpi-grid" style={{ marginBottom: 24 }}>
        <div
          className="fact-kpi-card"
          onClick={() => {
            setSelectedFolder(null);
            setSelectedType("all");
          }}
          role="button"
          style={{ cursor: "pointer" }}
        >
          <div className="fact-kpi-label">Total Pièces Documentées</div>
          <div className="fact-kpi-value" style={{ color: "#009688" }}>
            {statsData?.total_pieces ?? documents.length}
          </div>
          <div className="fact-kpi-sub">100% numérisées &amp; certifiées SYSCOHADA</div>
        </div>

        <div
          className="fact-kpi-card"
          onClick={() => {
            setSelectedFolder(null);
            setSelectedType("all");
          }}
          role="button"
          style={{ cursor: "pointer" }}
        >
          <div className="fact-kpi-label">Classées par l&apos;IA</div>
          <div className="fact-kpi-value" style={{ color: "#7c3aed" }}>
            {statsData?.classees_ia ?? documents.length}
          </div>
          <div className="fact-kpi-sub">Reconnaissance automatique OCR &amp; SYSCOHADA</div>
        </div>

        <div
          className="fact-kpi-card"
          onClick={() => {
            setSelectedFolder(null);
            setSelectedType("all");
          }}
          role="button"
          style={{ cursor: "pointer" }}
        >
          <div className="fact-kpi-label">Volume Financier Documenté</div>
          <div className="fact-kpi-value" style={{ color: "#0f172a" }}>
            {fmtMoney(statsData?.total_montant || 0)}
          </div>
          <div className="fact-kpi-sub">Justificatifs rattachés aux comptes</div>
        </div>
      </div>

      {/* Filter and View Switches Header */}
      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: 12,
          padding: "14px 18px",
          marginBottom: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        {/* Search Bar */}
        <div style={{ position: "relative", minWidth: 260, flex: 1, maxWidth: 400 }}>
          <FiSearch
            size={16}
            style={{
              position: "absolute",
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              color: "#94a3b8",
            }}
          />
          <input
            type="text"
            className="pieces-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par titre, tiers, montant..."
            style={{
              width: "100%",
              padding: "8px 12px 8px 36px",
              borderRadius: 8,
              fontSize: 13,
              background: "#f8fafc",
            }}
          />
        </div>

        {/* Filters and View Mode Switch */}
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          {/* Filter Type */}
          <select
            className="pieces-select"
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              fontSize: 12,
              background: "#ffffff",
              color: "#334155",
            }}
          >
            <option value="all">Tous les types de pièces</option>
            {DOC_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>

          {/* Filter Partner */}
          {partners.length > 0 && (
            <select
              className="pieces-select"
              value={selectedPartner}
              onChange={(e) => setSelectedPartner(e.target.value)}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                fontSize: 12,
                background: "#ffffff",
                color: "#334155",
              }}
            >
              <option value="all">Tous les partenaires</option>
              {partners.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          )}

          {/* View Mode Toggle */}
          <div
            style={{
              display: "inline-flex",
              border: "1px solid #cbd5e1",
              borderRadius: 8,
              overflow: "hidden",
              background: "#f1f5f9",
            }}
          >
            <button
              type="button"
              onClick={() => setViewMode("dossiers")}
              style={{
                padding: "7px 12px",
                border: "none",
                background: viewMode === "dossiers" ? "#009688" : "transparent",
                color: viewMode === "dossiers" ? "#ffffff" : "#475569",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              <FiFolder size={14} /> Dossiers
            </button>
            <button
              type="button"
              onClick={() => setViewMode("liste")}
              style={{
                padding: "7px 12px",
                border: "none",
                background: viewMode === "liste" ? "#009688" : "transparent",
                color: viewMode === "liste" ? "#ffffff" : "#475569",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              <FiList size={14} /> Liste
            </button>
            <button
              type="button"
              onClick={() => setViewMode("grille")}
              style={{
                padding: "7px 12px",
                border: "none",
                background: viewMode === "grille" ? "#009688" : "transparent",
                color: viewMode === "grille" ? "#ffffff" : "#475569",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              <FiGrid size={14} /> Grille
            </button>
          </div>
        </div>
      </div>

      {/* Folder Breadcrumb if a folder is selected */}
      {selectedFolder && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 16,
            background: "#e0f7fa",
            padding: "10px 16px",
            borderRadius: 8,
            color: "#009688",
            fontWeight: 600,
            fontSize: 13,
          }}
        >
          <button
            type="button"
            onClick={() => setSelectedFolder(null)}
            style={{
              background: "transparent",
              border: "none",
              color: "#009688",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              fontWeight: 700,
            }}
          >
            <FiArrowLeft size={16} /> Tous les dossiers
          </button>
          <span>/</span>
          <span>{selectedFolder}</span>
        </div>
      )}

      {/* Main Content by View Mode */}
      {viewMode === "dossiers" && !selectedFolder && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: 16,
          }}
        >
          {FOLDERS_SYSCOHADA.map((f) => {
            const summary = statsData?.folders_summary || {};
            const stats = summary[f.id] || summary[f.code] || { count: 0, total: 0 };
            const count = stats.count || 0;
            const total = stats.total || 0;

            return (
              <div
                key={f.id}
                onClick={() => setSelectedFolder(f.id)}
                style={{
                  background: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderRadius: 12,
                  padding: "20px 22px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  minHeight: 140,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = f.color;
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 6px 12px rgba(0,0,0,0.06)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e2e8f0";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.02)";
                }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 10,
                      background: f.bg,
                      color: f.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FiFolder size={22} />
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
                      {f.label}
                    </div>
                    <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                      Code SYSCOHADA : {f.code}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                    marginTop: 16,
                    paddingTop: 12,
                    borderTop: "1px solid #f1f5f9",
                  }}
                >
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#64748b" }}>
                    {count} {count > 1 ? "pièces" : "pièce"}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: f.color }}>
                    {fmtMoney(total)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* List Table View */}
      {(viewMode === "liste" || (viewMode === "dossiers" && selectedFolder)) && (
        <div
          style={{
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: 12,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
          }}
        >
          {isPiecesLoading || isPiecesFetching ? (
            <TableLoader
              message="Chargement des pièces comptables..."
              subMessage="Récupération des documents et classifications"
              minHeight="280px"
            />
          ) : filteredDocs.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: "#64748b", fontSize: 13 }}>
              <FiFolder size={36} color="#cbd5e1" style={{ margin: "0 auto 10px auto" }} />
              <div>Aucune pièce comptable trouvée pour ces critères.</div>
            </div>
          ) : (
            <div className="pieces-table-container">
              <table className="pieces-table">
                <thead>
                  <tr>
                    <th>Document</th>
                    <th>Type</th>
                    <th>Dossier SYSCOHADA</th>
                    <th>Partenaire</th>
                    <th>Date</th>
                    <th style={{ textAlign: "right" }}>Montant</th>
                    <th>Classification IA</th>
                    <th style={{ textAlign: "center" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocs.map((doc) => (
                    <tr
                      key={doc.id}
                      style={{ cursor: "pointer" }}
                      onClick={() => setPreviewDoc(doc)}
                    >
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ width: 32, height: 32, borderRadius: 8, background: "#e0f7fa", color: "#009688", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <FiFileText size={16} />
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, color: "#0f172a", fontSize: "12.5px" }}>{doc.title}</div>
                            <div style={{ fontSize: "10.5px", color: "#94a3b8", fontFamily: "monospace", marginTop: 1 }}>
                              {doc.fileName || `${doc.title}.pdf`} {doc.fileSize && `• ${doc.fileSize}`}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span
                          style={{
                            background: "#f1f5f9",
                            color: "#334155",
                            padding: "3px 8px",
                            borderRadius: 6,
                            fontSize: "11px",
                            fontWeight: 600,
                          }}
                        >
                          {doc.docType || doc.doc_type}
                        </span>
                      </td>
                      <td style={{ color: "#009688", fontWeight: 700, fontSize: "11.5px" }}>
                        {doc.folder || doc.dossier_syscohada}
                      </td>
                      <td style={{ fontWeight: 500, color: "#334155", fontSize: "12px" }}>
                        {doc.partnerName || doc.partenaire || ":"}
                      </td>
                      <td style={{ color: "#64748b", fontSize: "11.5px" }}>
                        {doc.date || doc.date_piece || ":"}
                      </td>
                      <td
                        style={{
                          textAlign: "right",
                          fontWeight: 700,
                          color: "#0f172a",
                          fontSize: "12.5px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {fmtMoney(doc.amount || doc.montant)}
                      </td>
                      <td>
                        <span
                          style={{
                            background: "#ede9fe",
                            color: "#7c3aed",
                            padding: "2px 8px",
                            borderRadius: 20,
                            fontSize: "10px",
                            fontWeight: 600,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <FiCpu size={11} />
                          <span>{doc.aiClassification || doc.classification_ia || "Vérifié SYSCOHADA"}</span>
                        </span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 5 }}>
                          <button
                            type="button"
                            onClick={() => handleComptabiliserPiece(doc)}
                            className="fact-action-btn"
                            style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer", padding: "3px 9px", fontSize: "11px", background: "#009688", color: "#fff", border: "none", borderRadius: 6, fontWeight: 600 }}
                            title="Comptabiliser via OCR (proposition d'écriture)"
                          >
                            <FiCpu size={12} style={{ flexShrink: 0 }} />
                            <span>Comptabiliser</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setPreviewDoc(doc)}
                            className="fact-action-btn fact-action-green"
                            style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer", padding: "3px 9px", fontSize: "11px" }}
                            title="Aperçu du document"
                          >
                            <FiEye size={12} style={{ flexShrink: 0 }} />
                            <span>Aperçu</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDownload(doc)}
                            className="fact-action-btn fact-action-outline"
                            style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer", padding: "3px 9px", fontSize: "11px" }}
                            title="Télécharger le fichier"
                          >
                            <FiDownload size={12} style={{ flexShrink: 0 }} />
                            <span>PDF</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(doc.id)}
                            className="fact-action-btn fact-action-red"
                            style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer", padding: "3px 7px", fontSize: "11px" }}
                            title="Supprimer la pièce"
                          >
                            <FiTrash2 size={12} style={{ flexShrink: 0 }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Grid View */}
      {viewMode === "grille" && !selectedFolder && (
        isPiecesLoading || isPiecesFetching ? (
          <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20 }}>
            <TableLoader
              message="Chargement des pièces comptables..."
              subMessage="Récupération des documents et aperçus"
              minHeight="280px"
            />
          </div>
        ) : filteredDocs.length === 0 ? (
          <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 40, textAlign: "center", color: "#64748b", fontSize: 13 }}>
            <FiFolder size={36} color="#cbd5e1" style={{ margin: "0 auto 10px auto" }} />
            <div>Aucune pièce comptable trouvée pour ces critères.</div>
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: 16,
            }}
          >
            {filteredDocs.map((doc) => (
            <div
              key={doc.id}
              onClick={() => setPreviewDoc(doc)}
              style={{
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                padding: 16,
                cursor: "pointer",
                boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: "#e0f7fa",
                      color: "#009688",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FiFileText size={18} />
                  </div>
                  <span
                    style={{
                      background: "#f1f5f9",
                      color: "#475569",
                      padding: "2px 8px",
                      borderRadius: 6,
                      fontSize: 10,
                      fontWeight: 600,
                    }}
                  >
                    {doc.docType || doc.doc_type}
                  </span>
                </div>
                <div style={{ fontWeight: 700, color: "#0f172a", fontSize: 14, marginTop: 10 }}>
                  {doc.title}
                </div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                  {doc.partnerName || doc.partenaire || "Partenaire non spécifié"}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  paddingTop: 10,
                  borderTop: "1px solid #f1f5f9",
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 700, color: "#009688" }}>
                  {fmtMoney(doc.amount || doc.montant)}
                </span>
                <span style={{ fontSize: 11, color: "#94a3b8" }}>{doc.date || "Récemment"}</span>
              </div>
              <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                <button
                  type="button"
                  onClick={() => handleComptabiliserPiece(doc)}
                  style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer", padding: "4px 10px", fontSize: "11px", background: "#009688", color: "#fff", border: "none", borderRadius: 6, fontWeight: 600 }}
                  title="Comptabiliser via OCR (proposition d'écriture)"
                >
                  <FiCpu size={12} style={{ flexShrink: 0 }} />
                  <span>Comptabiliser</span>
                </button>
              </div>
            </div>
          ))}
          </div>
        )
      )}

      {/* MODAL 1: SCANNER / IMPORTER AVEC IA & AJOUT MANUEL */}
      <Modal
        isOpen={showUploadModal}
        onRequestClose={closeUploadModal}
        contentLabel="Scanner / Importer une Pièce"
        className="Modal pieces-modal"
        overlayClassName="Overlay"
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: "#e0f7fa", color: "#009688", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FiUploadCloud size={20} />
            </div>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: "#0f172a" }}>
                Scanner / Importer une Pièce
              </h3>
              <p style={{ fontSize: 12, color: "#64748b", margin: 0 }}>
                L&apos;IA analysera le document, extraira le montant et le classera dans le bon dossier.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={closeUploadModal}
            className="modal-close-btn"
          >
            <FiX size={18} />
          </button>
        </div>

        <form onSubmit={handleUploadSubmit} className="pieces-form" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Dropzone with Live AI Scanning */}
          <div
            onClick={() => !isAiParsing && fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${isAiParsing ? "#7c3aed" : "#009688"}`,
              background: isAiParsing ? "#faf5ff" : formFile ? "#ecfdf5" : "#f0fdfa",
              borderRadius: 10,
              padding: "16px",
              textAlign: "center",
              cursor: isAiParsing ? "wait" : "pointer",
              transition: "all 0.2s ease",
            }}
          >
            {isAiParsing ? (
              <div style={{ padding: "8px 0" }}>
                <FiRefreshCw size={24} color="#7c3aed" style={{ animation: "spin 1s linear infinite", margin: "0 auto 6px auto" }} />
                <div style={{ fontSize: 13, fontWeight: 700, color: "#6d28d9" }}>
                  Analyse IA &amp; Extraction OCR en cours...
                </div>
                <div style={{ fontSize: 11, color: "#7c3aed", marginTop: 2 }}>
                  Reconnaissance du tiers, du montant et affectation SYSCOHADA...
                </div>
              </div>
            ) : (
              <>
                <FiUploadCloud size={24} color="#009688" style={{ margin: "0 auto 6px auto" }} />
                <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>
                  {formFile ? formFile.name : "Glissez-déposez un justificatif (PDF ou Image)"}
                </div>
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                  {formFile ? `${(formFile.size / 1024).toFixed(1)} Ko : Cliquez pour remplacer` : "Ou cliquez pour parcourir vos fichiers"}
                </div>
              </>
            )}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: "none" }}
              accept=".pdf,.png,.jpg,.jpeg"
            />
          </div>

          {/* AI Pre-fill Badge */}
          {aiConfidence && !isAiParsing && (
            <div
              style={{
                background: "#ecfdf5",
                border: "1px solid #a7f3d0",
                color: "#065f46",
                borderRadius: 8,
                padding: "8px 12px",
                fontSize: 11,
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <FiZap size={14} color="#059669" />
              <span>✨ {aiConfidence} : Champs pré-remplis, ajustables ci-dessous :</span>
            </div>
          )}

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>Titre de la pièce *</label>
            <input
              type="text"
              required
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              placeholder="Ex: Facture Fournisseur CIE"
              style={{ width: "100%", padding: "8px 10px", borderRadius: 6, fontSize: 13, marginTop: 4 }}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>Type de document</label>
              <select
                value={formDocType}
                onChange={(e) => setFormDocType(e.target.value)}
                style={{ width: "100%", padding: "8px 10px", borderRadius: 6, fontSize: 13, marginTop: 4, background: "#ffffff" }}
              >
                {DOC_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>Dossier SYSCOHADA *</label>
              <select
                value={formFolder}
                onChange={(e) => setFormFolder(e.target.value)}
                style={{ width: "100%", padding: "8px 10px", borderRadius: 6, fontSize: 13, marginTop: 4, background: "#ffffff" }}
              >
                {FOLDERS_SYSCOHADA.map((f) => (
                  <option key={f.id} value={f.id}>{f.id}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>Partenaire / Tiers</label>
              <input
                type="text"
                value={formPartner}
                onChange={(e) => setFormPartner(e.target.value)}
                placeholder="Ex: Orange CI, CIE..."
                style={{ width: "100%", padding: "8px 10px", borderRadius: 6, fontSize: 13, marginTop: 4 }}
              />
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>Montant TTC (FCFA)</label>
              <input
                type="number"
                value={formAmount}
                onChange={(e) => setFormAmount(e.target.value)}
                placeholder="Ex: 500000"
                style={{ width: "100%", padding: "8px 10px", borderRadius: 6, fontSize: 13, marginTop: 4 }}
              />
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 14 }}>
            <button
              type="button"
              onClick={closeUploadModal}
              disabled={isUploading}
              style={{ border: "1px solid #cbd5e1", background: "#ffffff", padding: "8px 18px", borderRadius: 31, fontSize: 13, fontWeight: 600, color: "#475569", cursor: "pointer" }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isUploading}
              style={{ border: "none", background: "#009688", color: "#ffffff", padding: "8px 20px", borderRadius: 31, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
            >
              {isUploading ? "Enregistrement..." : "Enregistrer la Pièce"}
            </button>
          </div>
        </form>
      </Modal>

      {/* MODAL 2: IMPORTER UNE PIÈCE AVEC IA (100% IDENTIQUE AU DESIGN) */}
      <Modal
        isOpen={showAiImportModal}
        onRequestClose={closeAiModal}
        contentLabel="Importer une pièce avec IA"
        className="Modal pieces-modal"
        overlayClassName="Overlay"
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "#ede9fe", color: "#7c3aed", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FiZap size={20} />
            </div>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 800, margin: 0, color: "#0f172a" }}>
                Importer une pièce comptable avec IA
              </h3>
              <p style={{ fontSize: 12, color: "#64748b", margin: 0 }}>
                Numérisation OCR &amp; classement automatique dans le plan SYSCOHADA.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={closeAiModal}
            className="modal-close-btn"
          >
            <FiX size={18} />
          </button>
        </div>

        {aiSuccess ? (
          <div style={{ padding: "32px 20px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 14, textAlign: "center" }}>
            <FiCheckCircle size={42} color="#16a34a" style={{ margin: "0 auto 10px auto" }} />
            <h4 style={{ fontSize: 15, fontWeight: 800, color: "#14532d", margin: "0 0 6px 0" }}>
              Pièce comptable importée et classée !
            </h4>
            <p style={{ fontSize: 12, color: "#15803d", margin: 0 }}>
              Document numérisé et rattaché au dossier SYSCOHADA avec succès.
            </p>
          </div>
        ) : isAiScanning ? (
          <div style={{ padding: "36px 20px", background: "#faf5ff", border: "1px solid #f3e8ff", borderRadius: 14, textAlign: "center" }}>
            <FiRefreshCw size={36} color="#7c3aed" style={{ animation: "spin 1.2s linear infinite", margin: "0 auto 14px auto" }} />
            <h4 style={{ fontSize: 15, fontWeight: 800, color: "#581c87", margin: "0 0 6px 0" }}>
              Analyse du document par l&apos;IA en cours...
            </h4>
            <p style={{ fontSize: 12, color: "#7e22ce", margin: 0 }}>
              Reconnaissance des montants, du tiers, des taxes et attribution du dossier comptable...
            </p>
          </div>
        ) : (
          <form onSubmit={handleAiImportSubmit} className="pieces-form" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Sélectionnez le Dossier Comptable SYSCOHADA cible
              </label>
              <select
                value={aiFolder}
                onChange={(e) => setAiFolder(e.target.value)}
                style={{ width: "100%", padding: "9px 12px", borderRadius: 8, fontSize: 13, marginTop: 4, background: "#f8fafc", fontWeight: 600, border: "1px solid #cbd5e1" }}
              >
                {FOLDERS_SYSCOHADA.map((f) => (
                  <option key={f.id} value={f.id}>{f.id}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Glissez votre document ou facture
              </label>
              <div
                onClick={() => aiFileInputRef.current?.click()}
                style={{
                  border: "2px dashed #d8b4fe",
                  background: "#faf5ff",
                  borderRadius: 12,
                  padding: "24px 16px",
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  marginTop: 4,
                }}
              >
                <FiUploadCloud size={30} color="#7c3aed" style={{ margin: "0 auto 8px auto" }} />
                <div style={{ fontSize: 13, fontWeight: 700, color: "#4c1d95" }}>
                  {aiFile ? aiFile.name : "Glissez votre facture ou reçu ici"}
                </div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                  {aiFile ? `${(aiFile.size / 1024).toFixed(1)} Ko : Cliquez pour remplacer` : "PDF, PNG, JPG scannés pris en charge"}
                </div>
                <input
                  type="file"
                  ref={aiFileInputRef}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      setAiFile(f);
                      if (!aiDocName) {
                        setAiDocName(f.name.replace(/\.[^/.]+$/, ""));
                      }
                    }
                  }}
                  style={{ display: "none" }}
                  accept=".pdf,.png,.jpg,.jpeg"
                />
              </div>
            </div>

            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Intitulé du document
              </label>
              <input
                type="text"
                required
                value={aiDocName}
                onChange={(e) => setAiDocName(e.target.value)}
                placeholder="Ex: Facture_SENELEC_Matin_Juin.pdf"
                style={{ width: "100%", padding: "9px 12px", borderRadius: 8, fontSize: 13, marginTop: 4, border: "1px solid #cbd5e1" }}
              />
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 6 }}>
              <button
                type="button"
                onClick={closeAiModal}
                style={{ border: "1px solid #cbd5e1", background: "#ffffff", padding: "8px 18px", borderRadius: 31, fontSize: 13, fontWeight: 600, color: "#475569", cursor: "pointer" }}
              >
                Annuler
              </button>
              <button
                type="submit"
                style={{
                  border: "none",
                  background: "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)",
                  color: "#ffffff",
                  padding: "9px 22px",
                  borderRadius: 31,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  boxShadow: "0 2px 8px rgba(124, 58, 237, 0.3)",
                }}
              >
                <FiZap size={14} color="#fde047" />
                <span>Analyser &amp; Classer (IA)</span>
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* MODAL 2b: IMPORT EN MASSE (chargement + resultat, reste ouvert si rejets) */}
      <Modal
        isOpen={showMassModal}
        onRequestClose={() => !massUploading && setShowMassModal(false)}
        contentLabel="Import en masse"
        className="Modal pieces-modal"
        overlayClassName="Overlay"
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "#ccfbf1", color: "#009688", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FiUploadCloud size={20} />
            </div>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 800, margin: 0, color: "#0f172a" }}>
                Import en masse
              </h3>
              <p style={{ fontSize: 12, color: "#64748b", margin: 0 }}>
                Analyse OCR de plusieurs fichiers et classement automatique.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => !massUploading && setShowMassModal(false)}
            className="modal-close-btn"
            disabled={massUploading}
          >
            <FiX size={18} />
          </button>
        </div>

        {massUploading ? (
          <div style={{ padding: "36px 20px", background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 14, textAlign: "center" }}>
            <FiRefreshCw size={36} color="#009688" style={{ animation: "spin 1.2s linear infinite", margin: "0 auto 14px auto" }} />
            <h4 style={{ fontSize: 15, fontWeight: 800, color: "#115e59", margin: "0 0 6px 0" }}>
              Analyse en cours...
            </h4>
            <p style={{ fontSize: 12, color: "#0f766e", margin: 0 }}>
              {massProgress || "Traitement des fichiers..."}
            </p>
            <p style={{ fontSize: 11, color: "#94a3b8", marginTop: 10, margin: "10px 0 0 0" }}>
              Cela peut prendre quelques secondes par fichier (OCR + classement).
            </p>
          </div>
        ) : massResult ? (
          massResult.ko === 0 && massResult.rejected === 0 ? (
            <div style={{ padding: "32px 20px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 14, textAlign: "center" }}>
              <FiCheckCircle size={42} color="#16a34a" style={{ margin: "0 auto 10px auto" }} />
              <h4 style={{ fontSize: 15, fontWeight: 800, color: "#14532d", margin: "0 0 6px 0" }}>
                {massResult.ok} pièce(s) importée(s) et classée(s) !
              </h4>
              <p style={{ fontSize: 12, color: "#15803d", margin: 0 }}>
                Documents analysés par OCR et rattachés au dossier SYSCOHADA.
              </p>
            </div>
          ) : (
            <div style={{ padding: "24px 18px", background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <FiAlertTriangle size={24} color="#ea580c" />
                <h4 style={{ fontSize: 15, fontWeight: 800, color: "#9a3412", margin: 0 }}>
                  {massResult.ok} importée(s), {massResult.rejected} rejetée(s), {massResult.ko} en échec
                </h4>
              </div>
              <p style={{ fontSize: 12, color: "#9a3412", margin: "0 0 12px 0" }}>
                {massResult.rejected > 0
                  ? "Certains fichiers ont été rejetés car ils ne semblent pas être des factures. Importez une vraie facture ou complétez manuellement la pièce."
                  : "Certains fichiers n'ont pas pu être importés. Réessayez ou vérifiez votre connexion."}
              </p>
              {massResult.rejectedFiles.length > 0 && (
                <div style={{ fontSize: 11, color: "#78350f", background: "#fef3c7", padding: "8px 10px", borderRadius: 8, marginBottom: 12, maxHeight: 80, overflowY: "auto" }}>
                  {massResult.rejectedFiles.map((f, i) => (
                    <div key={i} style={{ marginBottom: 2 }}>• {f}</div>
                  ))}
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button
                  type="button"
                  onClick={() => { setShowMassModal(false); setMassResult(null); }}
                  style={{ border: "1px solid #cbd5e1", background: "#ffffff", padding: "8px 18px", borderRadius: 31, fontSize: 13, fontWeight: 600, color: "#475569", cursor: "pointer" }}
                >
                  Fermer
                </button>
                <button
                  type="button"
                  onClick={() => { setShowMassModal(false); setMassResult(null); massInputRef.current?.click(); }}
                  style={{ border: "none", background: "#009688", color: "#ffffff", padding: "8px 18px", borderRadius: 31, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                >
                  Importer une vraie facture
                </button>
              </div>
            </div>
          )
        ) : null}
      </Modal>

      {/* MODAL 3: GOOGLE DRIVE SYNC (Désactivé pour la démo) */}
      {/* <Modal
        isOpen={showDriveModal}
        onRequestClose={() => setShowDriveModal(false)}
        contentLabel="Synchronisation Google Drive"
        className="Modal pieces-modal"
        overlayClassName="Overlay"
      >
        ...
      </Modal> */}

      {/* MODAL 4: APERÇU COMPLET & TÉLÉCHARGEMENT */}
      <Modal
        isOpen={!!previewDoc}
        onRequestClose={() => setPreviewDoc(null)}
        contentLabel="Aperçu de la Pièce Comptable"
        className="Modal pieces-modal pieces-modal-lg"
        overlayClassName="Overlay"
      >
        {previewDoc && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, borderBottom: "1px solid #f1f5f9", paddingBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: "#e0f7fa", color: "#009688", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <FiFileText size={22} />
                </div>
                <div>
                  <h3 style={{ fontSize: 17, fontWeight: 800, margin: 0, color: "#0f172a" }}>
                    {previewDoc.title}
                  </h3>
                  <div style={{ fontSize: 11, color: "#64748b", marginTop: 2, fontFamily: "monospace" }}>
                    {previewDoc.fileName || `${previewDoc.title}.pdf`} {previewDoc.fileSize && `• ${previewDoc.fileSize}`}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setPreviewDoc(null)}
                className="modal-close-btn"
              >
                <FiX size={18} />
              </button>
            </div>

            {/* AI Banner */}
            <div style={{ background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 10, padding: 12, marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
              <FiZap size={18} color="#7c3aed" />
              <div style={{ fontSize: 12, color: "#5b21b6" }}>
                <strong>Analyse &amp; Validation IA :</strong> {previewDoc.aiClassification || previewDoc.classification_ia || "Classé conforme selon le plan SYSCOHADA"}
              </div>
            </div>

            {/* Document Certificate Sheet */}
            <div
              style={{
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 12,
                padding: 18,
                fontFamily: "monospace",
                fontSize: 12,
                color: "#334155",
                lineHeight: 1.8,
                marginBottom: 20,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #e2e8f0", paddingBottom: 10, marginBottom: 10 }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 14, color: "#0f172a" }}>
                    {previewDoc.partnerName || previewDoc.partenaire || "PIÈCE COMPTABLE"}
                  </div>
                  <div style={{ fontSize: 10, color: "#64748b" }}>Document Numérisé &amp; Conforme SYSCOHADA</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>{previewDoc.date || previewDoc.date_piece || "2026"}</div>
                  <span style={{ background: "#e0f2fe", color: "#0284c7", padding: "2px 6px", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>
                    {previewDoc.docType || previewDoc.doc_type}
                  </span>
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Dossier Thématique :</span>
                <strong>{previewDoc.folder || previewDoc.dossier_syscohada}</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Statut de conformité :</span>
                <span style={{ color: "#16a34a", fontWeight: 700 }}>Classé &amp; Certifié</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #e2e8f0", paddingTop: 10, marginTop: 10, fontSize: 14 }}>
                <span style={{ fontWeight: 800, color: "#0f172a" }}>Montant Total :</span>
                <span style={{ fontWeight: 900, color: "#009688" }}>{fmtMoney(previewDoc.amount || previewDoc.montant)}</span>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 6 }}>
              <button
                type="button"
                onClick={() => handleDelete(previewDoc.id)}
                style={{
                  border: "1px solid #fecaca",
                  background: "#fee2e2",
                  color: "#dc2626",
                  padding: "8px 16px",
                  borderRadius: 31,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <FiTrash2 size={14} /> Supprimer
              </button>

              <div style={{ display: "flex", gap: 10 }}>
                <button
                  type="button"
                  onClick={() => setPreviewDoc(null)}
                  style={{ border: "1px solid #cbd5e1", background: "#ffffff", padding: "8px 18px", borderRadius: 31, fontSize: 13, fontWeight: 600, color: "#475569", cursor: "pointer" }}
                >
                  Fermer
                </button>
                <button
                  type="button"
                  onClick={() => handleDownload(previewDoc)}
                  style={{
                    border: "none",
                    background: "#009688",
                    color: "#ffffff",
                    padding: "8px 20px",
                    borderRadius: 31,
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <FiDownload size={14} /> Télécharger la pièce
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal Rapprochement pièces ↔ écritures (EC-18) */}
      <Modal
        isOpen={showRapprochementModal}
        onRequestClose={() => setShowRapprochementModal(false)}
        style={{
          overlay: { background: "rgba(0,0,0,0.5)", zIndex: 9999 },
          content: {
            maxWidth: "800px",
            margin: "auto",
            padding: "24px",
            borderRadius: "12px",
            maxHeight: "85vh",
            overflow: "auto",
          },
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#1e293b" }}>
            Rapprochement pièces GED ↔ écritures comptables
          </h3>
          <button onClick={() => setShowRapprochementModal(false)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20 }}>
            <FiX />
          </button>
        </div>

        {rapprochementLoading && (
          <div style={{ textAlign: "center", padding: 40, color: "#64748b" }}>
            <FiRefreshCw size={24} className="animate-spin" style={{ marginBottom: 8 }} />
            <p>Analyse en cours...</p>
          </div>
        )}

        {!rapprochementLoading && rapprochementData && (
          <>
            {/* Stats résumé */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
              <div style={{ background: "#dcfce7", padding: 12, borderRadius: 8, textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#166534" }}>{rapprochementData.stats?.matched_count || 0}</div>
                <div style={{ fontSize: 11, color: "#166534" }}>Rapprochés</div>
              </div>
              <div style={{ background: "#fef3c7", padding: 12, borderRadius: 8, textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#92400e" }}>{rapprochementData.stats?.pieces_orphelines_count || 0}</div>
                <div style={{ fontSize: 11, color: "#92400e" }}>Pièces sans écriture</div>
              </div>
              <div style={{ background: "#fee2e2", padding: 12, borderRadius: 8, textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#991b1b" }}>{rapprochementData.stats?.ecritures_orphelines_count || 0}</div>
                <div style={{ fontSize: 11, color: "#991b1b" }}>Écritures sans pièce</div>
              </div>
              <div style={{ background: "#fef2f2", padding: 12, borderRadius: 8, textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#7f1d1d" }}>{rapprochementData.stats?.doublons_count || 0}</div>
                <div style={{ fontSize: 11, color: "#7f1d1d" }}>Doublons suspects</div>
              </div>
            </div>

            {/* Pièces rapprochées */}
            {rapprochementData.matched?.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: "#166534", marginBottom: 8 }}>
                  <FiCheckCircle size={14} style={{ marginRight: 4 }} />
                  Pièces rapprochées ({rapprochementData.matched.length})
                </h4>
                <div style={{ maxHeight: 200, overflow: "auto", border: "1px solid #e2e8f0", borderRadius: 8 }}>
                  {rapprochementData.matched.map((m, i) => (
                    <div key={i} style={{ padding: 8, borderBottom: "1px solid #f1f5f9", fontSize: 12 }}>
                      <strong>{m.piece.titre}</strong> ({m.piece.partenaire || "N/A"}, {Number(m.piece.montant).toLocaleString("fr-FR")} FCFA, {m.piece.date || ":"})
                      <span style={{ color: "#166534" }}> ↔ </span>
                      Écriture : {m.ecriture.libelle} ({Number(m.ecriture.montant).toLocaleString("fr-FR")} FCFA, {m.ecriture.date || ":"})
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pièces orphelines */}
            {rapprochementData.pieces_orphelines?.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: "#92400e", marginBottom: 8 }}>
                  <FiFileText size={14} style={{ marginRight: 4 }} />
                  Pièces sans écriture ({rapprochementData.pieces_orphelines.length})
                </h4>
                <div style={{ maxHeight: 200, overflow: "auto", border: "1px solid #e2e8f0", borderRadius: 8 }}>
                  {rapprochementData.pieces_orphelines.map((p, i) => (
                    <div key={i} style={{ padding: 8, borderBottom: "1px solid #f1f5f9", fontSize: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span>
                        <strong>{p.titre}</strong> : {p.partenaire || "N/A"}, {Number(p.montant).toLocaleString("fr-FR")} FCFA, {p.date || ":"}
                        <span style={{ color: "#92400e", marginLeft: 8 }}>⚠ Justificatif non rattaché</span>
                      </span>
                      <button
                        type="button"
                        onClick={() => handleComptabiliserPiece(p)}
                        style={{ fontSize: 11, padding: "4px 10px", borderRadius: 6, border: "1px solid #92400e", background: "#fff", color: "#92400e", cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap", marginLeft: 8 }}
                      >
                        Comptabiliser
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Écritures orphelines */}
            {rapprochementData.ecritures_orphelines?.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: "#991b1b", marginBottom: 8 }}>
                  <FiFileText size={14} style={{ marginRight: 4 }} />
                  Écritures sans pièce justificative ({rapprochementData.ecritures_orphelines.length})
                </h4>
                <div style={{ maxHeight: 200, overflow: "auto", border: "1px solid #e2e8f0", borderRadius: 8 }}>
                  {rapprochementData.ecritures_orphelines.map((e, i) => (
                    <div key={i} style={{ padding: 8, borderBottom: "1px solid #f1f5f9", fontSize: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span>
                          <strong>{e.libelle}</strong> : {Number(e.montant).toLocaleString("fr-FR")} FCFA, {e.date || ":"}
                          <span style={{ color: "#991b1b", marginLeft: 8 }}>⚠ Pièce manquante</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => setAttachTarget(attachTarget?.id === e.id ? null : e)}
                          style={{ fontSize: 11, padding: "4px 10px", borderRadius: 6, border: "1px solid #991b1b", background: "#fff", color: "#991b1b", cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap", marginLeft: 8 }}
                        >
                          {attachTarget?.id === e.id ? "Annuler" : "Attacher"}
                        </button>
                      </div>
                      {attachTarget?.id === e.id && (
                        <div style={{ marginTop: 6, padding: 8, background: "#fef2f2", borderRadius: 6, border: "1px solid #fecaca" }}>
                          {(rapprochementData.pieces_orphelines || []).length === 0 ? (
                            <span style={{ color: "#64748b", fontSize: 11 }}>Aucune pièce orpheline disponible : importez d&apos;abord le justificatif.</span>
                          ) : (
                            rapprochementData.pieces_orphelines.map((p, j) => (
                              <div key={j} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0", fontSize: 11 }}>
                                <span>{p.titre} : {Number(p.montant).toLocaleString("fr-FR")} FCFA</span>
                                <button
                                  type="button"
                                  onClick={() => handleRattacherPiece(p)}
                                  style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, border: "none", background: "#991b1b", color: "#fff", cursor: "pointer" }}
                                >
                                  Choisir
                                </button>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Doublons suspects */}
            {rapprochementData.doublons?.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: "#7f1d1d", marginBottom: 8 }}>
                  <FiZap size={14} style={{ marginRight: 4 }} />
                  Doublons suspects ({rapprochementData.doublons.length})
                </h4>
                <div style={{ maxHeight: 200, overflow: "auto", border: "1px solid #e2e8f0", borderRadius: 8 }}>
                  {rapprochementData.doublons.map((d, i) => (
                    <div key={i} style={{ padding: 8, borderBottom: "1px solid #f1f5f9", fontSize: 12 }}>
                      {[d.ecriture_1, d.ecriture_2].filter(Boolean).map((ec, j) => (
                        <div key={j} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span>
                            <strong>{ec.libelle}</strong> : {Number(ec.montant).toLocaleString("fr-FR")} FCFA, {ec.date || ":"} <em>(#{ec.id})</em>
                          </span>
                          {j === 0 && (
                            <span style={{ color: "#7f1d1d", fontSize: 11, fontWeight: 600 }}>⚠ Doublon possible</span>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Aucun résultat */}
            {rapprochementData.stats?.matched_count === 0 &&
             rapprochementData.stats?.pieces_orphelines_count === 0 &&
             rapprochementData.stats?.ecritures_orphelines_count === 0 &&
             rapprochementData.stats?.doublons_count === 0 && (
              <div style={{ textAlign: "center", padding: 40, color: "#64748b" }}>
                <FiCheckCircle size={32} style={{ marginBottom: 8, color: "#16a34a" }} />
                <p>Tout est rapproché. Aucune anomalie détectée.</p>
              </div>
            )}
          </>
        )}
      </Modal>

      {/* Modale OCR : proposition d'ecriture corrigible (MVP CECA) */}
      <EcritureProposeeModal
        open={ecritureModal.open}
        piece={ecritureModal.piece}
        dossier_id={dossier_id}
        onClose={() => setEcritureModal({ open: false, piece: null })}
        onValidated={handleEcritureValidee}
      />

      {/* Modale de confirmation de suppression (comme dans les transactions) */}
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onRequestClose={() => {
          setIsConfirmModalOpen(false);
          setPieceToDelete(null);
        }}
        onConfirm={handleDeleteConfirmation}
        title="Supprimer la pièce comptable"
        message="Êtes-vous sûr de vouloir supprimer cette pièce comptable ?"
      />
    </div>
  );
};

export default PiecesPage;
